import { useEffect, useCallback, useRef } from 'react';
import scrollIntoViewIfNeeded from 'scroll-into-view-if-needed';
import { ELID_ISSUE_HEADER } from '@atlassian/jira-issue-view-common-constants/src/index.tsx';
import type { IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';

type TimeoutId = ReturnType<typeof setTimeout>;

const INTERVAL = 100;
const TIMEOUT = 10000;
// account for Bento's sticky header
const SCROLL_PADDING = 34;

export const getRef = () => {
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	const nodes = Array.from(document.querySelectorAll(`#${ELID_ISSUE_HEADER} + div h2`));
	// take the last H2 inside Bento (comments section header)
	return nodes.length ? nodes[nodes.length - 1] : null;
};

export const useScrollToComments = ({ issueKey }: { issueKey: IssueKey }) => {
	const intervalId = useRef<TimeoutId>();
	const timeoutId = useRef<TimeoutId>();
	const isTimeout = useRef(false);

	const waitForElement = useCallback(() => {
		if (isTimeout.current) {
			return;
		}

		const ref = getRef();
		if (ref) {
			// sometimes the browser can't immediately scroll,
			// so we add a small timeout here to let it render Bento first
			setTimeout(() => {
				ref &&
					scrollIntoViewIfNeeded(ref, {
						scrollMode: 'always',
						block: 'start',
						inline: 'nearest',
						behavior: (actions) =>
							actions.forEach(({ el, top }) => {
								// eslint-disable-next-line no-param-reassign
								el.scrollTop = top - SCROLL_PADDING;
							}),
					});
			}, 300);
		} else {
			intervalId.current = setTimeout(waitForElement, INTERVAL);
		}
	}, []);

	useEffect(() => {
		clearTimeout(intervalId.current);
		clearTimeout(timeoutId.current);
		isTimeout.current = false;

		// clear the interval in case comments section never shows up
		timeoutId.current = setTimeout(() => {
			isTimeout.current = true;
		}, TIMEOUT);

		// kick off the interval if issueKey changes + on mount
		intervalId.current = setTimeout(waitForElement, INTERVAL);

		// clear interval on unmount
		return () => {
			clearTimeout(intervalId.current);
			clearTimeout(timeoutId.current);
		};
	}, [waitForElement, issueKey]);
};
